namespace Solovis.Reports.Parameters.AttributionReport {

  export interface AttributionOtherOptionsConfigurations {
    bookValues?: boolean;
    groupByColumnHoldings?: boolean;
    allLabel?: string;
    removeAllLine?: boolean;
    removeEmptyComponents?: boolean;
    twrSettingsSourceAsset?: any;
  }

  export interface AttributionOtherOptionsColumns {
    endingValue?: boolean;
    localCurrencyCode?: boolean;
    currencyCode?: boolean;
    fullLabelPath?: boolean;
    isGroup?: boolean;
    compositeTagId?: boolean;
    compositeTagCode?: boolean;
    groupNodeId?: boolean;
    parentGroupNodeId?: boolean;
  }

  export interface AttributionOtherOptions {
    configurations?: AttributionOtherOptionsConfigurations;
    columns?: AttributionOtherOptionsColumns;
  }

  const getDefaultOptions = (): AttributionOtherOptions => ({
    configurations: {},
    columns: {}
  });

  const DEFAULT_REMOVE_EMPTY_COMPONENTS = true;

  /**
   * Stored value when it is a boolean, else the default (including a missing configurations object).
   * Shared by the dialog and the request builder.
   */
  export function resolveRemoveEmptyComponents(configurations?: AttributionOtherOptionsConfigurations): boolean {
    const value = configurations?.removeEmptyComponents;
    return typeof value === "boolean" ? value : DEFAULT_REMOVE_EMPTY_COMPONENTS;
  }

  /** The values the Edit Other Options dialog shows as initial values. */
  export function getOtherOptionsInitialValues(options?: AttributionOtherOptions): {
    configurations: AttributionOtherOptionsConfigurations;
    columns: AttributionOtherOptionsColumns;
  } {
    const configurations = options?.configurations ?? {};
    const columns = options?.columns ?? {};
    return {
      configurations: {
        bookValues: !!configurations.bookValues,
        groupByColumnHoldings: !!configurations.groupByColumnHoldings,
        allLabel: configurations.allLabel ?? "",
        removeAllLine: !!configurations.removeAllLine,
        removeEmptyComponents: resolveRemoveEmptyComponents(configurations),
        twrSettingsSourceAsset: configurations.twrSettingsSourceAsset
      },
      columns: {
        endingValue: !!columns.endingValue,
        localCurrencyCode: !!columns.localCurrencyCode,
        currencyCode: !!columns.currencyCode,
        fullLabelPath: !!columns.fullLabelPath,
        isGroup: !!columns.isGroup,
        compositeTagId: !!columns.compositeTagId,
        compositeTagCode: !!columns.compositeTagCode,
        groupNodeId: !!columns.groupNodeId,
        parentGroupNodeId: !!columns.parentGroupNodeId
      }
    };
  }

  // Help text for the Additional Configurations options. Descriptive drafts to be confirmed with Product.
  const ConfigHelp = {
    bookValues: "Use booked (as-reported) values rather than blended/restated values when computing weights and returns.",
    groupByColumnHoldings: "Group line items by their underlying column holdings rather than by the selected Group By property.",
    allLabel: "Override the label shown on the 'All' summary row (defaults to 'All').",
    removeAllLine: "Remove the 'All' summary row from the attribution table.",
    removeEmptyComponents: "Hide groups or line items that have no data for the selected period.",
    twrSettingsSourceAsset: "Asset whose TWR settings are used as the source for return calculations."
  };

  /**
   * "Other Options" parameters-bar control for the Attribution Report. Renders a Configure link
   * that opens the Edit Other Options modal (AR-011-2).
   */
  export class AttributionOtherOptionsParameterControl extends Solovis.UI.BaseControl {
    private _options: AttributionOtherOptions;

    constructor(options) {
      super(options);
      this._options = (typeof options.initialValue !== "undefined" && options.initialValue !== null)
        ? options.initialValue
        : getDefaultOptions();
    }

    public doRender() {
      const self = this;

      const configureLink = $("<a>").text("Configure").appendTo(this.container);
      configureLink.click(function () { self._showEditor(); });

      const editLink = $("<a>").appendTo(this.container)
        .css("padding-left", "5px")
        .html("<span class='fa fa-pencil'></span>");
      editLink.click(function () { self._showEditor(); });
    }

    private _showEditor() {
      const editorDialog = new EditOtherOptionsDialog({
        options: this._options,
        saveCallback: (data: AttributionOtherOptions) => this.setParameterValue(data)
      });
      editorDialog.show();
    }

    public _setCurrentOptions(data) {
      this._options = data ?? getDefaultOptions();
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, AttributionOtherOptionsParameterControl, this._setCurrentOptions, newValue, more);
    }

    public getParameterValue() { return Solovis.deepCopy(this._options); }

    public notifyParameterValueChanged() { $(this).trigger("parameterValueChanged"); }
  }


  export class EditOtherOptionsDialog {
    private _opts: AttributionOtherOptions;
    private _saveCallback: (data: AttributionOtherOptions) => void;
    private _formBuilder: Solovis.UI.InputControls.FormBuilder;

    public constructor(options: { options: AttributionOtherOptions; saveCallback: (data: AttributionOtherOptions) => void; }) {
      this._opts = options.options ? Solovis.deepCopy(options.options) : getDefaultOptions();
      this._saveCallback = options.saveCallback;
    }

    public show() {
      const { configurations, columns } = getOtherOptionsInitialValues(this._opts);

      const formBuilder = new Solovis.UI.InputControls.FormBuilder({
        container: $("<div>"),
        width: 620,
        labelColumnWidth: 240
      });
      this._formBuilder = formBuilder;

      // Additional Configurations
      formBuilder.addEntry({ name: "configurationsHeader", label: "Additional Configurations", isGroupHeader: true });

      formBuilder.addEntry({
        name: "configurations.bookValues", label: "Book Values", help: ConfigHelp.bookValues,
        initialValue: !!configurations.bookValues, editor: { type: "bool" }
      });
      formBuilder.addEntry({
        name: "configurations.groupByColumnHoldings", label: "Group by Column Holdings", help: ConfigHelp.groupByColumnHoldings,
        initialValue: !!configurations.groupByColumnHoldings, editor: { type: "bool" }
      });
      formBuilder.addEntry({
        name: "configurations.allLabel", label: "'All' Label", help: ConfigHelp.allLabel,
        initialValue: configurations.allLabel ?? "", editor: { type: "text" }
      });
      formBuilder.addEntry({
        name: "configurations.removeAllLine", label: "Remove All Line", help: ConfigHelp.removeAllLine,
        initialValue: !!configurations.removeAllLine, editor: { type: "bool" }
      });
      formBuilder.addEntry({
        name: "configurations.removeEmptyComponents", label: "Remove Empty Components", help: ConfigHelp.removeEmptyComponents,
        initialValue: configurations.removeEmptyComponents, editor: { type: "bool" }
      });
      formBuilder.addEntry({
        name: "configurations.twrSettingsSourceAsset", label: "Twr Settings Source Asset", help: ConfigHelp.twrSettingsSourceAsset,
        initialValue: configurations.twrSettingsSourceAsset,
        editor: { type: "parameter", parameterOptions: { parameterType: "entityOrInvestment" } }
      });

      // Additional Columns
      formBuilder.addEntry({ name: "columnsHeader", label: "Additional Columns", isGroupHeader: true });

      const addColumnBool = (name: keyof AttributionOtherOptionsColumns, label: string) => {
        formBuilder.addEntry({
          name: "columns." + name,
          label: label,
          initialValue: !!columns[name],
          editor: { type: "bool" }
        });
      };

      addColumnBool("endingValue", "Ending Value");
      addColumnBool("localCurrencyCode", "Local Currency Code");
      addColumnBool("currencyCode", "Currency Code");
      addColumnBool("fullLabelPath", "Full Label Path");
      addColumnBool("isGroup", "Is Group");
      addColumnBool("compositeTagId", "Composite Tag Id");
      addColumnBool("compositeTagCode", "Composite Tag Code");
      addColumnBool("groupNodeId", "Group Node Id");
      addColumnBool("parentGroupNodeId", "Parent Group Node Id");

      formBuilder.setupDone();

      formBuilder.showAsDialog({
        title: "Edit Other Options",
        desiredWidth: 660,
        desiredHeight: 700,
        showSaveCancel: true,
        saveButtonText: "OK",
        saveCallback: () => {
          const values = this._formBuilder.getValues();
          this._saveCallback({
            configurations: values.configurations ?? {},
            columns: values.columns ?? {}
          });
          return true;
        }
      });
    }
  }
}
